/**
 * One person's non-billable time, opened out by what it was booked against.
 *
 * THE SCOPE IS NARROWER THAN THE ROW THAT OPENED IT, and the dialog has to say
 * so. The engine's detail query excludes Client Billable entries outright, so
 * these lines sum to the person's non-billable plus time-off hours and never to
 * their total: Justin Pemberton's board row reads 2,208.00 h and this table
 * totals 1,696.50 h, the difference being exactly his 511.50 billable hours.
 * Left unstated, that gap reads as hours the page lost. So the summary strip
 * above the table carries all four buckets from the row -- the engine's own
 * figures, not a split taken here -- and the table states what it covers.
 *
 * Hours only, everywhere. There is no rate and no dollar column, and none may
 * be added: TimeEntry.CostAmount is populated on about a third of rows, so
 * anything derived from it under-reports by two thirds.
 */
import { Info } from "lucide-react";
import { DrillInDialog } from "@/components/profitability/DrillInDialog";
import { DataTable } from "@/components/shared/data-table";
import { ErrorState } from "@/components/shared/error-state";
import { useClientSort } from "@/hooks/use-client-sort";
import { useInternalTimeDetail } from "@/hooks/use-internal-time";
import { COLUMN_HELP } from "@/lib/column-help";
import { hours as formatHours, percent } from "@/lib/format";
import type { InternalTimeDetailRow, InternalTimeRow } from "@/lib/profitability-api";

const EMPTY_DETAIL: InternalTimeDetailRow[] = [];

function Figure({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold tabular-nums">{value}</p>
      {note ? <p className="text-xs text-muted-foreground">{note}</p> : null}
    </div>
  );
}

export function InternalTimeDrillInDialog({
  person,
  range,
  onOpenChange,
}: {
  person: InternalTimeRow | null;
  range?: string;
  onOpenChange: (open: boolean) => void;
}) {
  const { data, isLoading, isError, error, refetch } = useInternalTimeDetail(
    person?.person_id ?? null,
    range,
  );
  const detail = data?.detail;
  const rows = detail?.rows ?? EMPTY_DETAIL;
  const sorted = useClientSort(
    rows,
    (row, key) => row[key as keyof InternalTimeDetailRow] as string | number | null,
    { key: "hours", dir: "desc" },
  );

  return (
    <DrillInDialog
      open={person !== null}
      onOpenChange={onOpenChange}
      title={person ? person.person : ""}
      description={
        person
          ? `${formatHours(person.non_billable_hours)} h non-billable and ${formatHours(
              person.time_off_hours,
            )} h of time off, in the selected months`
          : undefined
      }
    >
      {person ? (
        <div className="space-y-4">
          {/* The row's own four buckets, so the table below is read against the
              whole rather than mistaken for it. Billable is here precisely
              because the table cannot show it. */}
          <div className="grid gap-4 sm:grid-cols-4">
            <Figure
              label="Billable"
              value={`${formatHours(person.billable_hours)} h`}
              note={
                person.billable_pct === null
                  ? "logged only time off"
                  : `${percent(person.billable_pct)} of worked hours`
              }
            />
            <Figure label="Internal" value={`${formatHours(person.internal_hours)} h`} />
            <Figure
              label="Client, non-billable"
              value={`${formatHours(person.client_non_billable_hours)} h`}
            />
            {/* Last and labelled as not work, so it is not read as a fifth kind
                of unbilled effort sitting beside the two that are. */}
            <Figure
              label="Time off"
              value={`${formatHours(person.time_off_hours)} h`}
              note="not work, and not non-billable"
            />
          </div>

          <p className="flex items-start gap-2 rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
            <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            {/* The engine's `scope` now states this in general terms, and this
                sentence is kept anyway because it names THIS person's three
                figures -- which is what lets a reader check the arithmetic
                rather than take the scope on trust. `scope` cannot serve as the
                fallback below: it arrives with the very data it would stand in
                for, so before the request lands there is no scope either. */}
            {detail ? (
              <span>
                This table is the non-billable time and the time off only &mdash; billable entries
                are excluded from it at source.{" "}
                {/* When there is no billable work the two figures are equal, and
                    explaining a difference of zero reads as a page that has lost
                    track of its own arithmetic. */}
                {person.billable_hours === 0
                  ? `It totals ${formatHours(
                      detail.non_billable_and_time_off_hours,
                    )} h, which is the whole of the ${formatHours(
                      person.total_hours,
                    )} h on the board row: none of this person's time was billable.`
                  : `It totals ${formatHours(
                      detail.non_billable_and_time_off_hours,
                    )} h against the ${formatHours(
                      person.total_hours,
                    )} h on the board row, and the difference is the ${formatHours(
                      person.billable_hours,
                    )} h of billable work.`}
              </span>
            ) : (
              <span>
                This table is the non-billable time and the time off only &mdash; billable entries
                are excluded from it at source.
              </span>
            )}
          </p>

          {isError ? (
            <ErrorState message={(error as Error)?.message} onRetry={() => refetch()} />
          ) : (
            <DataTable
              loading={isLoading}
              sort={sorted.sort}
              sortDir={sorted.sortDir}
              onSort={sorted.toggleSort}
              headers={[
                { label: "Entry type", sortKey: "entry_type", help: COLUMN_HELP.timeEntryType },
                { label: "Offering", sortKey: "offering", help: COLUMN_HELP.benchOffering },
                { label: "Project", sortKey: "project", help: COLUMN_HELP.project },
                {
                  label: "Hours",
                  sortKey: "hours",
                  className: "text-right",
                  help: COLUMN_HELP.hours,
                },
              ]}
            >
              {sorted.rows.map((row) => (
                <tr
                  // Grouped by all four in the engine, so the four together are
                  // the identity -- `demand_id` alone repeats across entry types
                  // and is null on every PTO line.
                  key={`${row.entry_type}|${row.offering}|${row.project}|${row.demand_id ?? "none"}`}
                  className="border-b border-border last:border-0 hover:bg-muted/30"
                >
                  <td className="px-4 py-3 font-medium">{row.entry_type}</td>
                  <td className="px-4 py-3 text-muted-foreground">{row.offering}</td>
                  <td className="px-4 py-3">{row.project}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums">
                    {formatHours(row.hours)} h
                  </td>
                </tr>
              ))}
            </DataTable>
          )}

          {!isLoading && !isError && rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No non-billable time and no time off in the selected months.
            </p>
          ) : null}
        </div>
      ) : null}
    </DrillInDialog>
  );
}
